import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, ExternalLink, Loader2, QrCode, Clock, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PaymentProvider, PaymentStatus } from '@/hooks/usePaymentFlow';
import { useTranslation } from '@/contexts/I18nContext';

interface PaymentProcessingModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentProvider: PaymentProvider;
  paymentUrl: string;
  orderId: string;
  onCheckStatus: () => void;
  isPolling: boolean;
  paymentStatus: PaymentStatus;
}

export const PaymentProcessingModal: React.FC<PaymentProcessingModalProps> = ({
  isOpen,
  onClose,
  paymentProvider,
  paymentUrl,
  orderId,
  onCheckStatus,
  isPolling,
  paymentStatus,
}) => {
  const { t } = useTranslation();

  const handleOpenInBrowser = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  const generateQRCodeUrl = (data: string) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(data)}`;
  };

  const renderPaymentStatusAlert = () => {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 border-blue-200',
        message: t('purchase.waitingForPayment')
      },
      payed: {
        icon: Loader2,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50 border-yellow-200',
        message: t('purchase.paymentReceived')
      },
      success: {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200',
        message: t('purchase.paymentSuccessful')
      },
      failed: {
        icon: ExternalLink,
        color: 'text-red-600',
        bgColor: 'bg-red-50 border-red-200',
        message: t('purchase.paymentFailed')
      },
      expired: {
        icon: Clock,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50 border-orange-200',
        message: t('purchase.paymentExpired')
      },
    };

    const config = statusConfig[paymentStatus];
    const Icon = config.icon;

    return (
      <Alert className={cn("border-2", config.bgColor)}>
        <Icon className={cn("w-4 h-4", config.color, paymentStatus === 'payed' && "animate-spin")} />
        <AlertDescription className={cn("font-medium", config.color)}>
          {config.message}
        </AlertDescription>
      </Alert>
    );
  };

  const isAlipay = paymentProvider === 'alipay';
  const isPolar = paymentProvider === 'polar';

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-lg bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-900/20 flex flex-col max-h-screen">
        <SheetHeader className="space-y-4 pb-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-xl shadow-lg",
              isAlipay 
                ? "bg-gradient-to-r from-blue-500 to-cyan-500" 
                : "bg-gradient-to-r from-purple-500 to-pink-500"
            )}>
              {isAlipay ? (
                <QrCode className="w-6 h-6 text-white" />
              ) : (
                <Globe className="w-6 h-6 text-white" />
              )}
            </div>
            <SheetTitle className={cn(
              "text-2xl font-bold bg-clip-text text-transparent",
              isAlipay
                ? "bg-gradient-to-r from-blue-600 to-cyan-600"
                : "bg-gradient-to-r from-purple-600 to-pink-600"
            )}>
              {isAlipay ? t('purchase.scanToPay') : t('purchase.completePayment')}
            </SheetTitle>
          </div>
          <SheetDescription className="text-gray-600 dark:text-gray-300 text-base">
            {isAlipay
              ? t('purchase.alipayPaymentDescription')
              : t('purchase.polarPaymentDescription')
            }
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="mt-8 space-y-6">
          {/* QR Code for Alipay or Loading indicator for Polar */}
          <div className="flex justify-center">
            <div className="relative group">
              {isAlipay && paymentUrl ? (
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-all duration-300"></div>
                  
                  <div className="relative border-4 border-white dark:border-gray-800 rounded-2xl p-4 bg-white dark:bg-gray-800 shadow-2xl">
                    <img
                      src={generateQRCodeUrl(paymentUrl)}
                      alt="Payment QR Code"
                      className="w-64 h-64 rounded-lg cursor-pointer transition-transform hover:scale-105"
                      onClick={handleOpenInBrowser}
                    />
                  </div>
                </div>
              ) : isPolar ? (
                <div className="w-72 h-72 border-4 border-purple-200 dark:border-purple-700 rounded-2xl flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                  <Globe className="w-16 h-16 text-purple-500 mb-4" />
                  <div className="text-center">
                    <p className="text-lg font-semibold text-purple-700 dark:text-purple-300 mb-2">
                      {t('purchase.paymentWindowOpened')}
                    </p>
                    <p className="text-sm text-purple-600 dark:text-purple-400">
                      {t('purchase.completePaymentInNewWindow')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-72 h-72 border-4 border-gray-200 dark:border-gray-700 rounded-2xl flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                  <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
                </div>
              )}
              
              {(isPolling || paymentStatus === 'payed') && (
                <div className="absolute -top-2 -right-2">
                  <div className={cn(
                    "w-6 h-6 rounded-full animate-pulse shadow-lg",
                    paymentStatus === 'payed' 
                      ? "bg-gradient-to-r from-yellow-500 to-amber-500" 
                      : isAlipay 
                        ? "bg-gradient-to-r from-blue-500 to-cyan-500"
                        : "bg-gradient-to-r from-purple-500 to-pink-500"
                  )}></div>
                </div>
              )}
            </div>
          </div>

          {/* Open in Browser Button */}
          {paymentUrl && (
            <Button
              variant="outline"
              onClick={handleOpenInBrowser}
              className="w-full py-3 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-lg transition-all duration-300"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              {isAlipay ? t('purchase.openInBrowser') : t('purchase.reopenPaymentWindow')}
            </Button>
          )}

          {/* Payment Status */}
          {renderPaymentStatusAlert()}

          {/* Order Info */}
          <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-xl">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('purchase.orderId')}</div>
            <div className="font-mono text-sm font-medium text-gray-800 dark:text-gray-200">{orderId}</div>
          </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-3 flex-shrink-0">
          <Button 
            onClick={onCheckStatus} 
            className={cn(
              "w-full py-3 text-white font-medium shadow-lg transition-all duration-300",
              isAlipay 
                ? "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            )}
            disabled={isPolling}
          >
            {isPolling ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('purchase.checkingStatus')}
              </>
            ) : (
              t('purchase.checkPaymentStatus')
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full py-3 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {t('purchase.close')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
